/**
 * @file Leads list view — phone-first lead management for field callbacks
 */

import {
  getLeads, searchLeads, addLead, deleteLead, updateLeadStatus, addLeadNote,
  STATUS_OPTIONS, SOURCE_OPTIONS, timeAgo
} from '@services/leads.js';
import { push } from '@core/router.js';
import { showModal, closeModal } from '@components/modal.js';
import { toast } from '@components/toast.js';
import { setTabBadge } from '@components/bottom-nav.js';

let _searchTerm = '';
let _activeFilter = 'all';
let _sortBy = 'newest';

const FILTER_CHIPS = [
  { key: 'all',        label: 'All' },
  { key: 'new',        label: 'New' },
  { key: 'follow_up',  label: 'Follow Up' },
  { key: 'good_fit',   label: 'Good Fit' },
  { key: 'wants_book', label: 'Wants Book' },
  { key: 'contacted',  label: 'Contacted' },
  { key: 'quoted',     label: 'Quoted' },
  { key: 'won',        label: 'Won' },
  { key: 'cold',       label: 'Cold' },
  { key: 'dead',       label: 'Dead' },
];

function getFilteredLeads() {
  let leads = _searchTerm ? searchLeads(_searchTerm) : getLeads();

  if (_activeFilter !== 'all') {
    leads = leads.filter(l => l.status === _activeFilter);
  }

  if (_sortBy === 'newest') {
    leads.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  } else if (_sortBy === 'last_contacted') {
    leads.sort((a, b) => (b.lastContacted || b.updatedAt || '').localeCompare(a.lastContacted || a.updatedAt || ''));
  } else if (_sortBy === 'status') {
    leads.sort((a, b) => {
      const pa = (STATUS_OPTIONS[a.status] || {}).priority || 99;
      const pb = (STATUS_OPTIONS[b.status] || {}).priority || 99;
      return pa - pb;
    });
  } else if (_sortBy === 'alpha') {
    leads.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  return leads;
}

function render() {
  const leads = getFilteredLeads();
  const totalLeads = getLeads().length;

  const newCount = getLeads().filter(l => l.status === 'new' || l.status === 'follow_up').length;
  setTimeout(() => setTabBadge('leads', newCount), 0);

  return `
    <div class="view-header">
      <div>
        <h1>Leads</h1>
        <div class="view-header-sub">${totalLeads} total</div>
      </div>
      <button class="btn btn-gold btn-sm" id="add-lead-btn">+ Add</button>
    </div>

    <div class="search-wrap">
      <span class="search-icon">\uD83D\uDD0D</span>
      <input type="text" id="leads-search" placeholder="Search leads..." value="${esc(_searchTerm)}" />
    </div>

    <div class="filter-chips" id="leads-filters">
      ${FILTER_CHIPS.map(f =>
        `<span class="chip ${_activeFilter === f.key ? 'active' : ''}" data-filter="${f.key}">${f.label}</span>`
      ).join('')}
    </div>

    <div class="leads-sort-row">
      <select id="leads-sort" class="leads-sort-select">
        <option value="newest"${_sortBy === 'newest' ? ' selected' : ''}>Newest First</option>
        <option value="last_contacted"${_sortBy === 'last_contacted' ? ' selected' : ''}>Last Contacted</option>
        <option value="status"${_sortBy === 'status' ? ' selected' : ''}>By Priority</option>
        <option value="alpha"${_sortBy === 'alpha' ? ' selected' : ''}>A-Z</option>
      </select>
      <span class="leads-result-count">${leads.length} lead${leads.length !== 1 ? 's' : ''}</span>
    </div>

    <div class="lead-list" id="lead-list">
      ${leads.length > 0
        ? leads.map(l => renderLeadCard(l)).join('')
        : `<div class="empty-state">
             <div class="empty-state-icon">\uD83D\uDCDE</div>
             <div class="empty-state-text">No leads found</div>
           </div>`
      }
    </div>
  `;
}

function renderLeadCard(lead) {
  const statusObj = STATUS_OPTIONS[lead.status] || STATUS_OPTIONS['new'];
  const badgeClass = statusObj.color || 'badge-gray';
  const contacted = timeAgo(lead.lastContacted || lead.updatedAt);

  return `
    <div class="lead-card" data-lead-id="${lead.id}">
      <div class="lead-card-top">
        <div class="lead-card-info">
          <div class="lead-card-name">${esc(lead.name || 'Unnamed')}</div>
          ${lead.phone ? `<a href="tel:${esc(lead.phone)}" class="lead-card-phone" onclick="event.stopPropagation();">\uD83D\uDCDE ${esc(lead.phone)}</a>` : ''}
        </div>
        <span class="badge ${badgeClass}">${statusObj.label}</span>
      </div>

      <div class="lead-card-meta">
        ${lead.source ? `<span class="lead-card-source">${esc(lead.source)}</span>` : ''}
        <span class="lead-card-contacted">${contacted}</span>
        ${lead.inPipeline ? '<span class="badge badge-blue" style="font-size:0.6rem;">In Pipeline</span>' : ''}
      </div>

      <div class="lead-card-actions">
        ${lead.phone ? `
          <a href="tel:${esc(lead.phone)}" class="lead-quick-btn lead-quick-call" onclick="event.stopPropagation();" title="Call">
            <span>\uD83D\uDCDE</span>
            <span>Call</span>
          </a>
          <a href="sms:${esc(lead.phone)}" class="lead-quick-btn lead-quick-text" onclick="event.stopPropagation();" title="Text">
            <span>\u2709\uFE0F</span>
            <span>Text</span>
          </a>
        ` : ''}
        <button class="lead-quick-btn lead-quick-note" data-note-id="${lead.id}" title="Add Note">
          <span>\uD83D\uDCDD</span>
          <span>Note</span>
        </button>
      </div>
    </div>
  `;
}

function mount() {
  // Search
  const search = document.getElementById('leads-search');
  if (search) {
    search.addEventListener('input', (e) => {
      _searchTerm = e.target.value;
      refreshView();
    });
  }

  // Filter chips
  const filters = document.getElementById('leads-filters');
  if (filters) {
    filters.addEventListener('click', (e) => {
      const chip = e.target.closest('.chip');
      if (!chip) return;
      _activeFilter = chip.dataset.filter;
      refreshView();
    });
  }

  // Sort
  const sort = document.getElementById('leads-sort');
  if (sort) {
    sort.addEventListener('change', (e) => {
      _sortBy = e.target.value;
      refreshView();
    });
  }

  // Add lead button
  document.getElementById('add-lead-btn')?.addEventListener('click', showAddLeadModal);

  // Lead card taps + quick note buttons
  const list = document.getElementById('lead-list');
  if (list) {
    list.addEventListener('click', (e) => {
      // Quick note button
      const noteBtn = e.target.closest('.lead-quick-note');
      if (noteBtn) {
        e.stopPropagation();
        showQuickNoteModal(noteBtn.dataset.noteId);
        return;
      }
      // Skip link taps (call/text)
      if (e.target.closest('a')) return;
      // Card tap -> detail
      const card = e.target.closest('.lead-card');
      if (card) push('lead-detail', { leadId: card.dataset.leadId });
    });
  }
}

function showAddLeadModal() {
  const sourceOpts = SOURCE_OPTIONS.map(s =>
    `<option value="${s}">${s}</option>`
  ).join('');

  showModal('Add Lead', `
    <div class="form-group">
      <label>Name *</label>
      <input type="text" id="new-lead-name" placeholder="Full name" />
    </div>
    <div class="form-group">
      <label>Phone *</label>
      <input type="tel" id="new-lead-phone" placeholder="(555) 123-4567" />
    </div>
    <div class="form-group">
      <label>Email</label>
      <input type="email" id="new-lead-email" placeholder="email@example.com" />
    </div>
    <div class="form-group">
      <label>Address</label>
      <input type="text" id="new-lead-address" placeholder="Street address" />
    </div>
    <div class="form-group">
      <label>Source</label>
      <select id="new-lead-source">
        <option value="">-- select --</option>
        ${sourceOpts}
      </select>
    </div>
    <div class="form-group">
      <label>Notes</label>
      <textarea id="new-lead-notes" rows="3" placeholder="What do they need?"></textarea>
    </div>
  `, [
    { label: 'Cancel', onClick: closeModal },
    { label: 'Save', type: 'gold', onClick: () => {
      const name = document.getElementById('new-lead-name')?.value?.trim();
      const phone = document.getElementById('new-lead-phone')?.value?.trim();
      if (!name) { toast('Name is required', 'error'); return; }
      if (!phone) { toast('Phone is required', 'error'); return; }

      addLead({
        name,
        phone,
        email: document.getElementById('new-lead-email')?.value?.trim() || '',
        address: document.getElementById('new-lead-address')?.value?.trim() || '',
        source: document.getElementById('new-lead-source')?.value || '',
        notes: document.getElementById('new-lead-notes')?.value?.trim() || '',
      });

      closeModal();
      toast(`${name} added`, 'success');
      refreshView();
    }},
  ]);

  setTimeout(() => document.getElementById('new-lead-name')?.focus(), 200);
}

function showQuickNoteModal(leadId) {
  showModal('Quick Note', `
    <div class="form-group">
      <textarea id="quick-note-text" rows="3" placeholder="How did it go?"></textarea>
    </div>
    <div class="form-group">
      <label>Update Status?</label>
      <select id="quick-note-status">
        <option value="">No change</option>
        ${Object.entries(STATUS_OPTIONS).map(([k, v]) =>
          `<option value="${k}">${v.label}</option>`
        ).join('')}
      </select>
    </div>
  `, [
    { label: 'Cancel', onClick: closeModal },
    { label: 'Save', type: 'gold', onClick: () => {
      const text = document.getElementById('quick-note-text')?.value?.trim();
      const newStatus = document.getElementById('quick-note-status')?.value;
      if (!text) { toast('Enter a note', 'error'); return; }

      addLeadNote(leadId, text, 'phone');
      if (newStatus) updateLeadStatus(leadId, newStatus);

      closeModal();
      toast('Note saved', 'success');
      refreshView();
    }},
  ]);

  setTimeout(() => document.getElementById('quick-note-text')?.focus(), 200);
}

function refreshView() {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render();
    mount();
  }
}

function unmount() {}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default { render, mount, unmount };
